import { isEmpty } from 'lodash';
import { v4 } from 'uuid';
import { TASK_MODEL } from '../../services/entity/task';
import { DEFAULT_EMPTY_VALUE } from '../constants';
import { ICON_TEXT_TYPE } from '../constants/Map';
import { TASK_STATUS } from '../constants/task';
import { convertIdToValue } from '../details';
import { mapIconSelector } from 'utils/formFields/task';
import { DB_NULL } from 'utils/constants';

export const getStatusValue = (list, key) => {
  return convertIdToValue({
    list,
    key,
    constant: TASK_STATUS,
    defaultValue: DEFAULT_EMPTY_VALUE,
  });
};

export const setAssetName = asset => {
  if (!asset) {
    return DEFAULT_EMPTY_VALUE;
  } else {
    let assetIdPrefix = '';
    let assetTypeIdPrefix = '';

    if (asset.assetId !== DEFAULT_EMPTY_VALUE) {
      assetIdPrefix = asset?.assetId ? `(${asset.assetId})` : '';
    }

    if (asset.type?.assetTypeId !== DEFAULT_EMPTY_VALUE) {
      assetTypeIdPrefix = asset.type?.assetTypeId
        ? `(${asset.type?.assetTypeId})`
        : '';
    }

    return `${assetIdPrefix} ${asset.name}: ${assetTypeIdPrefix} ${asset.type?.name}`;
  }
};

export const findTask = ({ tasks, id }) => {
  if (isEmpty(tasks)) {
    return undefined;
  }

  const task = tasks.tasks.find(item => item.id === id);

  return task;
};
export const getUserStringForList = (users, assignees) => {
  if (assignees === undefined || assignees.length === 0) {
    return 'Any available';
  }
  if (users === undefined && users.length === 0) {
    return DEFAULT_EMPTY_VALUE;
  }
  // loop through and create a string of names ',' separated
  let res = '';
  for (let i = 0; i < assignees.length; i += 1) {
    //check the name exists
    const current = users[assignees[i]];
    let name = users[assignees[i]];
    if (current === undefined) {
      name = DEFAULT_EMPTY_VALUE;
    }
    if (i > 0) {
      //add a comma
      res = res.concat(', ');
    }
    //add the name
    res = res.concat(name);
  }
  return res;
};
export const getAssignee = ({ assigneeId, users }) => {
  if (!assigneeId) {
    //goLang understands 'UNSET' which refers to null value when updating the database
    return {
      [TASK_MODEL.assignee.id]: DB_NULL,
      [TASK_MODEL.assigneeFullName.id]: DB_NULL,
    };
  }

  const assignee = isEmpty(users)
    ? assigneeId.name || null
    : users[assigneeId?.id];

  return assignee
    ? {
        [TASK_MODEL.assignee.id]: assigneeId.id ? assigneeId.id : assigneeId,
        [TASK_MODEL.assigneeFullName.id]: assignee,
      }
    : { [TASK_MODEL.assignee.id]: null };
};

export const getTaskIconTextType = ({ showPolyLines } = {}) => {
  return showPolyLines ? ICON_TEXT_TYPE.number : ICON_TEXT_TYPE.fillOnly;
};

export const getTaskLocationMarker = ({
  id = v4(),
  lat,
  lng,
  text,
  seqNumber,
  surfaceUuid,
  icon,
  showPolylines,
  subtaskId,
  locationId,
}) => {
  if (locationId) {
    return {
      id: locationId,
      lat,
      lng,
      text,
      seqNumber,
      surfaceUuid,
      subtaskId,
      ...(!showPolylines && { icon }),
    };
  }
  return {
    id,
    lat,
    lng,
    text,
    seqNumber,
    surfaceUuid,
    subtaskId,
    ...(!showPolylines && { icon }),
  };
};

export const assignNewSiteToSubtask = ({ rootTaskId, tasks, tasksPayload }) => {
  const hasNewSite = tasks.find(
    item => item.id === rootTaskId && item.siteData,
  );

  if (hasNewSite) {
    const newSiteId = tasksPayload.find(item => item.id === rootTaskId);

    return !newSiteId
      ? tasksPayload
      : tasksPayload.reduce((acc, cur) => {
          return acc.concat({
            ...cur,
            ...(cur.id !== rootTaskId && { siteId: newSiteId.siteId }),
          });
        }, []);
  }
  return tasksPayload;
};

export const replaceExistingLocation = ({
  existingLocations,
  location,
  index,
}) => {
  const updatedLocation = [...existingLocations];
  updatedLocation[index] = {
    ...updatedLocation[index],
    lat: location.lat,
    lng: location.lng,
  };

  return { additionalMarkers: updatedLocation };
};

export const arrangeLocationBySeqNumber = ({ childList, locationId }) => {
  const additionalMarkers = childList
    .sort((n1, n2) => n1.seqNumber - n2.seqNumber)
    .map((l, index) => ({
      ...l,
      text: index + 1,
    }));

  // get current child location sequence number
  const { text, seqNumber } = additionalMarkers.find(
    item => item.id === locationId,
  );

  return { additionalMarkers, text, seqNumber };
};

export const addLocationToParent = ({
  defaultIndex,
  formProps,
  childFormValue,
  formItem,
}) => {
  const {
    additionalMarkers: child,
    [formItem.field]: childList = [],
  } = formProps.values;

  // assign default index if child object is not created yet
  const currentMarkers = isEmpty(child) ? [] : [...child];
  const indexList = childList.findIndex(item => item.id === childFormValue.id);
  const index = indexList > -1 ? indexList : defaultIndex;
  const [location] = childFormValue.location;

  currentMarkers.push(
    getTaskLocationMarker({
      id: location.id,
      text: index,
      icon: mapIconSelector(formProps.values.taskType),
      seqNumber: index,
      showPolylines: childFormValue.subtasksSequentialOrder,
      lat: location.lat,
      lng: location.lng,
      surfaceUuid: childFormValue.siteSurfaceImages.id,
      subtaskId: childFormValue.id,
    }),
  );

  currentMarkers.sort((a, b) => a.seqNumber - b.seqNumber);

  return { additionalMarkers: currentMarkers };
};

export const deleteLocationifExist = ({
  formProps,
  childFormValue,
  formItem,
}) => {
  const subtaskList = formProps.values[formItem.field];

  const previousValue =
    subtaskList && subtaskList.find(item => item.id === childFormValue.id);

  // remove marker from parent task if not empty
  if (previousValue && !isEmpty(previousValue.location)) {
    const locationId = previousValue.location[0].id;
    const additionalMarkers = formProps.values.additionalMarkers.filter(
      item => item.id !== locationId,
    );

    return { additionalMarkers };
  }

  return { additionalMarkers: undefined };
};

export const transformLocationInParent = ({
  defaultIndex,
  formItem,
  formProps, // parent values
  childFormValue, // current edit values
  editModeValues,
}) => {
  // delete old marker location
  if (
    formProps.values.additionalMarkers &&
    editModeValues?.location &&
    editModeValues.location.length > 0
  ) {
    formProps.values.additionalMarkers = formProps.values.additionalMarkers.filter(
      f => f.id !== editModeValues.location[0].id,
    );
  }

  if (!childFormValue.location) return [];

  const childLocation = !isEmpty(childFormValue.location)
    ? childFormValue.location[0]
    : [];

  if (childLocation.length === 0) {
    return deleteLocationifExist({
      formProps,
      childFormValue,
      formItem,
    });
  }

  const { additionalMarkers } = formProps.values;

  const existingLocationInParent =
    !isEmpty(additionalMarkers) && Array.isArray(additionalMarkers)
      ? [...additionalMarkers]
      : [];

  const locationInParentIndex = existingLocationInParent.findIndex(
    item => item.id === childLocation.id,
  );

  if (locationInParentIndex > -1) {
    return replaceExistingLocation({
      existingLocations: existingLocationInParent,
      location: childFormValue.location[0],
      index: locationInParentIndex,
    });
  }

  return addLocationToParent({
    defaultIndex,
    formItem,
    formProps,
    childFormValue,
  });
};
